/**
 * Hook de inicialização da aplicação
 * Inicializa sistemas Platform e Lifecycle
 */

import { useEffect, useState } from 'react';
import { PlatformInitializer } from '../../../platform';
import { LifecycleManager } from '../../lifecycle';

interface AppInitializationState {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  platformReady: boolean;
  lifecycleReady: boolean;
}

export const useAppInitialization = () => {
  const [state, setState] = useState<AppInitializationState>({
    isInitialized: false,
    isLoading: true,
    error: null,
    platformReady: false,
    lifecycleReady: false
  });

  useEffect(() => {
    let isMounted = true;

    const initializeApp = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        console.log('🚀 Inicializando aplicação...');

        // Inicializa sistema de plataforma
        await PlatformInitializer.initialize();
        if (!isMounted) return;

        setState(prev => ({ ...prev, platformReady: true }));

        // Inicializa sistema de lifecycle (que usa platform)
        await LifecycleManager.initialize();
        if (!isMounted) return;

        setState(prev => ({ 
          ...prev, 
          lifecycleReady: true,
          isInitialized: true,
          isLoading: false 
        }));

        console.log('✅ Aplicação inicializada com sucesso');

        // Log do status final
        const status = LifecycleManager.getSystemStatus();
        console.log('📊 Status dos sistemas:', status);

      } catch (error) {
        console.error('❌ Erro na inicialização:', error);
        
        if (isMounted) {
          setState(prev => ({
            ...prev,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            isLoading: false
          }));
        }
      }
    };

    initializeApp();

    // Cleanup na desmontagem
    return () => {
      isMounted = false;
      
      // Cleanup dos sistemas se necessário
      if (state.isInitialized) {
        LifecycleManager.cleanup();
        PlatformInitializer.cleanup();
      }
    };
  }, []);

  // Função para retry em caso de erro
  const retry = async () => {
    setState(prev => ({ ...prev, error: null, isLoading: true }));
    
    try {
      await PlatformInitializer.initialize();
      await LifecycleManager.initialize();
      
      setState(prev => ({
        ...prev,
        isInitialized: true,
        isLoading: false,
        platformReady: true,
        lifecycleReady: true
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false
      }));
    }
  };

  return {
    ...state,
    retry
  };
};
